import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EmploymentInformationStep = ({ data, personalDetails, onComplete, onBack, onCancel }) => {
  const [formData, setFormData] = useState({
    employmentStartDate: data?.employmentStartDate || '',
    positionTitle: data?.positionTitle || '',
    department: data?.department || '',
    employmentType: data?.employmentType || 'full_time',
    baseSalary: data?.baseSalary || '',
    payFrequency: data?.payFrequency || 'fortnightly'
  });
  const [errors, setErrors] = useState({});
  const [contributions, setContributions] = useState({
    employeeContribution: 0,
    employerContribution: 0,
    totalContribution: 0
  });

  const employmentTypeOptions = [
    { value: 'full_time', label: 'Full-Time' },
    { value: 'part_time', label: 'Part-Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'casual', label: 'Casual' }
  ];

  const payFrequencyOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'fortnightly', label: 'Fortnightly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const departmentOptions = [
    { value: 'administration', label: 'Administration' },
    { value: 'finance', label: 'Finance' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'operations', label: 'Operations' },
    { value: 'it', label: 'Information Technology' },
    { value: 'sales', label: 'Sales & Marketing' },
    { value: 'customer_service', label: 'Customer Service' },
    { value: 'logistics', label: 'Logistics' },
    { value: 'other', label: 'Other' }
  ];

  // Calculate NASFUND contributions
  useEffect(() => {
    if (formData?.baseSalary) {
      const salary = parseFloat(formData?.baseSalary);
      if (!isNaN(salary) && salary > 0) {
        const employeeRate = 0.06; // 6%
        const employerRate = 0.084; // 8.4%
        
        const employeeContribution = salary * employeeRate;
        const employerContribution = salary * employerRate;
        const totalContribution = employeeContribution + employerContribution;

        setContributions({
          employeeContribution: employeeContribution?.toFixed(2),
          employerContribution: employerContribution?.toFixed(2),
          totalContribution: totalContribution?.toFixed(2)
        });
      }
    } else {
      setContributions({
        employeeContribution: 0,
        employerContribution: 0,
        totalContribution: 0
      });
    }
  }, [formData?.baseSalary]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.employmentStartDate) {
      newErrors.employmentStartDate = 'Employment start date is required';
    } else {
      const startDate = new Date(formData?.employmentStartDate);
      const today = new Date();
      if (startDate > today) {
        newErrors.employmentStartDate = 'Start date cannot be in the future';
      }
    }

    if (!formData?.positionTitle?.trim()) {
      newErrors.positionTitle = 'Position title is required';
    }

    if (!formData?.department) {
      newErrors.department = 'Department is required';
    }

    if (!formData?.employmentType) {
      newErrors.employmentType = 'Employment type is required';
    }

    if (!formData?.baseSalary) {
      newErrors.baseSalary = 'Base salary is required';
    } else {
      const salary = parseFloat(formData?.baseSalary);
      if (isNaN(salary) || salary <= 0) {
        newErrors.baseSalary = 'Please enter a valid salary amount';
      } else if (salary < 3.50) {
        newErrors.baseSalary = 'Salary must meet PNG minimum wage (K3.50/hour)';
      }
    }

    if (!formData?.payFrequency) {
      newErrors.payFrequency = 'Pay frequency is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onComplete?.({
        ...formData,
        contributions
      });
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Employment Information</h2>
        <p className="text-gray-600">
          Enter employment details for {personalDetails?.fullName || 'employee'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Employment Start Date */}
        <Input
          label="Employment Start Date"
          type="date"
          required
          value={formData?.employmentStartDate}
          onChange={(e) => handleChange('employmentStartDate', e?.target?.value)}
          error={errors?.employmentStartDate}
        />

        {/* Position Title */}
        <Input
          label="Position Title"
          type="text"
          required
          value={formData?.positionTitle}
          onChange={(e) => handleChange('positionTitle', e?.target?.value)}
          error={errors?.positionTitle}
          placeholder="e.g., Senior Accountant, HR Manager"
        />

        {/* Department and Employment Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Department"
            required
            options={departmentOptions}
            value={formData?.department}
            onChange={(value) => handleChange('department', value)}
            error={errors?.department}
            placeholder="Select department"
          />

          <Select
            label="Employment Type"
            required
            options={employmentTypeOptions}
            value={formData?.employmentType}
            onChange={(value) => handleChange('employmentType', value)}
            error={errors?.employmentType}
          />
        </div>

        {/* Salary and Pay Frequency */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Base Salary (PNG Kina)"
              type="number"
              step="0.01"
              required
              value={formData?.baseSalary}
              onChange={(e) => handleChange('baseSalary', e?.target?.value)}
              error={errors?.baseSalary}
              placeholder="0.00"
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum wage: K3.50/hour
            </p>
          </div>

          <Select
            label="Pay Frequency"
            required
            options={payFrequencyOptions}
            value={formData?.payFrequency}
            onChange={(value) => handleChange('payFrequency', value)}
            error={errors?.payFrequency}
          />
        </div>

        {/* NASFUND Contribution Calculation */}
        {formData?.baseSalary && parseFloat(formData?.baseSalary) > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Icon name="Calculator" size={20} className="text-blue-600 mr-2" />
              <h3 className="font-semibold text-blue-900">NASFUND Contribution Calculation</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Employee Contribution (6%)</p>
                <p className="text-2xl font-bold text-gray-900">
                  K{contributions?.employeeContribution}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Employer Contribution (8.4%)</p>
                <p className="text-2xl font-bold text-gray-900">
                  K{contributions?.employerContribution}
                </p>
              </div>
              <div className="bg-emerald-100 rounded-lg p-4">
                <p className="text-sm text-emerald-700 mb-1">Total Contribution</p>
                <p className="text-2xl font-bold text-emerald-900">
                  K{contributions?.totalContribution}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-3">
              Per {formData?.payFrequency === 'weekly' ? 'week' : formData?.payFrequency === 'fortnightly' ? 'fortnight' : 'month'}
            </p>
          </div>
        )}

        {/* Compliance Check */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="AlertCircle" size={20} className="text-amber-600 mr-3 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-900 mb-1">NASFUND Registration Deadline</p>
              <p className="text-sm text-amber-700">
                Employers must register new employees with NASFUND within 30 days of employment commencement. 
                Ensure all information is accurate to avoid penalties.
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
          >
            <Icon name="ArrowLeft" size={16} className="mr-2" />
            Back
          </Button>
          <Button
            type="submit"
            variant="primary"
            className="flex-1"
          >
            Continue to Beneficiary Setup
            <Icon name="ArrowRight" size={16} className="ml-2" />
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EmploymentInformationStep;